// Environmental objective detail (ISO 14001). Inline-editable fields including the status,
// and a delete control (hard-delete an on-track objective with no progress, else archive as
// missed to keep the tracking history). Office/admin.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError, type EnvObjectiveItem, type ObjectiveStatus } from "../../lib/api";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { OBJECTIVE_STATUS_LABEL } from "./environmentalUi";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { RecordHeader } from "../../components/RecordHeader";
import { StatusPill } from "../../components/StatusPill";
import { statusEntry } from "../../lib/statusRegistry";
import { formatDate } from "../../lib/format";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function EnvironmentalObjectiveDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [objective, setObjective] = useState<EnvObjectiveItem | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setState("loading");
      try {
        setObjective(await api.environmentalObjectives.get(id));
        setState("ready");
      } catch (err) {
        if (!opts?.silent) setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
      }
    },
    [id],
  );
  useEffect(() => {
    void load();
  }, [load]);

  if (state === "loading") return <Loader shape="panel" className="p-6" />;
  if (state === "notfound") return <p className="p-6 text-center text-ele-body">That objective could not be found.</p>;
  if (state === "error" || !objective) return <p role="alert" className="p-6 text-center text-ele-strong">Could not load this objective. Please try again.</p>;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <RecordHeader
        className="mb-4"
        title={objective.ref}
        backTo="/office/environmental-records"
        backLabel="Back to environmental records"
        status={
          <>
            <StatusPill word={OBJECTIVE_STATUS_LABEL[objective.status] ?? objective.status} tone={statusEntry("objective", objective.status).tone} />
            <span className="text-sm text-ele-body">Target {formatDate(objective.targetDate)}</span>
          </>
        }
      />

      <EditPanel objective={objective} onSaved={() => load({ silent: true })} />
      <DeleteSection objective={objective} onDeleted={() => navigate("/office/environmental-records")} />
    </div>
  );
}

function EditPanel({ objective, onSaved }: { objective: EnvObjectiveItem; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState(seed(objective));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(seed(objective));
  }, [objective]);

  const set = (name: keyof ReturnType<typeof seed>) => (e: { target: { value: string } }) => {
    setForm((f) => ({ ...f, [name]: e.target.value }));
    setSaved(false);
  };

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.environmentalObjectives.update(objective.id, {
        objective: form.objective.trim() || undefined,
        target: form.target.trim() || undefined,
        targetDate: form.targetDate || undefined,
        owner: form.owner.trim() || null,
        status: form.status as ObjectiveStatus,
        progressNotes: form.progressNotes.trim() || null,
      });
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
        <label className="sm:col-span-2">
          <span className={labelCls}>Objective</span>
          <input aria-label="Objective" value={form.objective} onChange={set("objective")} className={inputCls} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Measurable target</span>
          <input aria-label="Target" value={form.target} onChange={set("target")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Target date</span>
          <input aria-label="Target date" type="date" value={form.targetDate} onChange={set("targetDate")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Owner</span>
          <input aria-label="Owner" value={form.owner} onChange={set("owner")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Status</span>
          <select aria-label="Status" value={form.status} onChange={set("status")} className={inputCls}>
            {(Object.keys(OBJECTIVE_STATUS_LABEL) as ObjectiveStatus[]).map((s) => (
              <option key={s} value={s}>
                {OBJECTIVE_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Progress notes</span>
          <textarea aria-label="Progress notes" value={form.progressNotes} onChange={set("progressNotes")} rows={3} className={inputCls} />
        </label>
        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
          {saved && (
            <span role="status" className="text-sm text-ele-green">
              Saved.
            </span>
          )}
        </div>
      </form>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

function DeleteSection({ objective, onDeleted }: { objective: EnvObjectiveItem; onDeleted: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Mirror the server rule: an on-track objective with no progress hard-deletes; anything
  // tracked (moved off on_track, or with progress notes) archives (marked missed).
  const willArchive = objective.status !== "on_track" || Boolean(objective.progressNotes && objective.progressNotes.trim());

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api.environmentalObjectives.remove(objective.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not delete this objective.");
      setBusy(false);
    }
  }

  return (
    <section className="rounded-ele border border-ele-hairline bg-ele-surface-soft p-5">
      <Button
        variant="destructive"
        size="sm"
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        {willArchive ? "Archive objective" : "Delete objective"}
      </Button>
      <ConfirmDeleteModal
        open={open}
        variant={willArchive ? "archive" : "destructive"}
        title={willArchive ? "Archive this objective?" : "Delete this objective?"}
        message={
          willArchive
            ? "This objective has tracking history, so it will be archived (marked missed) and kept, not deleted."
            : "This on-track objective has no progress recorded, so it will be permanently deleted. This cannot be undone."
        }
        confirmLabel={willArchive ? "Archive objective" : "Delete objective"}
        busy={busy}
        error={error}
        onConfirm={confirm}
        onCancel={() => {
          if (!busy) {
            setOpen(false);
            setError(null);
          }
        }}
      />
    </section>
  );
}

function seed(o: EnvObjectiveItem) {
  return {
    objective: o.objective,
    target: o.target,
    targetDate: o.targetDate ? o.targetDate.slice(0, 10) : "",
    owner: o.owner ?? "",
    status: o.status as string,
    progressNotes: o.progressNotes ?? "",
  };
}
